import { convertNumToMinSec } from "./../../utils/helpers/convertNumToMinSec";
import { FileTypeE } from "./../../models/enums";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DEFAULT_VERSION_DATA } from "../../utils/constants";
import { getLoggedUser } from "../../utils/helpers/tokenStorage";
import { ProjectT, VersionT, SelectOptionT } from "./../../models/models";

interface InitialStateI {
  deliveryWizardStep: 1 | 2 | 3;
  delivery: ProjectT;
  editableVersionIndex: number;
  campaignDetails: any;
  isDirty: boolean;
  isShortEdit: boolean;
  selectedAdvertiser?: SelectOptionT | null;
  selectedBrand?: SelectOptionT | null;
  selectedMediaCompany?: SelectOptionT | null;
  selectedProductionCompany?: SelectOptionT | null;
  selectedIndustry?: SelectOptionT | null;
}

const defaultDeliveryData = {
  Id: null,
  Name: "",
  BrandId: "-1",
  IndustryId: "-1",
  AdvertiserId: "-1",
  ProductionLocationIsrael: true,
  SpeakerNames: "",
  ProjectComment: "",
  ParticipantNames: "",
  PostProductionCompanyId: "-1",
  BroadcastingDate: undefined,
  Versions: [DEFAULT_VERSION_DATA],
  ProjectType: "VideoAd",
  ProductionCompanyId: "-1",
  addToExist: 0,
  campaignRetry: true,
  AdvertisingAgencyId: null,
  AdvertisingAgencyName: "",
  IsCopyOperation: false,
  Campaign: {
    CampaignId: null,
  },
  BroadcasterIds: undefined,
  broadcasters: [],
};

const initialState: InitialStateI = {
  deliveryWizardStep: 1,
  delivery: defaultDeliveryData as any,
  editableVersionIndex: 0,
  campaignDetails: null,
  isDirty: false,
  isShortEdit: false,
};

const deliveryWizardSlice = createSlice({
  name: "deliveryWizard",
  initialState,
  reducers: {
    updateDeliveryData: (state, action: PayloadAction<any>) => {
      state.delivery = { ...state.delivery, ...action.payload };
    },
    setDeliveryData(state, action: PayloadAction<ProjectT>) {
      state.delivery = action.payload;
    },
    setPostProductionCompanyId(state, action: PayloadAction<number>) {
      state.delivery.PostProductionCompanyId = action.payload;
    },
    setProductionCompanyId(state, action: PayloadAction<number>) {
      state.delivery.ProductionCompanyId = action.payload;
    },
    setProductionLocation(state, action: PayloadAction<boolean>) {
      state.delivery.ProductionLocationIsrael = action.payload;
    },
    setDeliveryWizardStep(state, action: PayloadAction<1 | 2 | 3>) {
      state.deliveryWizardStep = action.payload;
    },
    setDeliveryDescription(state, action: PayloadAction<string>) {
      state.delivery.ProjectComment = action.payload;
    },
    setVersions(state, action: PayloadAction<VersionT[]>) {
      state.delivery.Versions = action.payload;
    },
    setDestinations(state, action: PayloadAction<number[]>) {
      state.delivery.BroadcasterIds = action.payload.join(",");
    },
    removerVersionByIndex(state, action: PayloadAction<number>) {
      state.delivery.Versions.splice(action.payload, 1);
    },
    removeVersionByAdId(state, action: PayloadAction<string>) {
      state.delivery.Versions = state.delivery.Versions.filter(
        (version) => version.AdId !== action.payload
      );
    },
    addVersion(state, action: PayloadAction<VersionT>) {
      state.delivery.Versions.push(action.payload);
    },
    addEmptyVersion(state) {
      state.delivery.Versions.push(DEFAULT_VERSION_DATA as any);
      state.editableVersionIndex = state.delivery.Versions.length - 1;
    },
    addDestination: (state, action: PayloadAction<number>) => {
      state.delivery.BroadcasterIds = state.delivery.BroadcasterIds
        ? [...state.delivery.BroadcasterIds.split(","), action.payload].join()
        : String(action.payload);
    },
    removeDestination: (state, action: PayloadAction<number>) => {
      state.delivery.BroadcasterIds = state.delivery.BroadcasterIds.split(",")
        .filter((item) => +item !== +action.payload)
        .join(",");
    },
    clearAll(state) {
      state.delivery = defaultDeliveryData as any;
      state.editableVersionIndex = 0;
      state.isDirty = false;
    },
    setNextStep(state) {
      state.deliveryWizardStep < 3
        ? state.deliveryWizardStep++
        : (state.deliveryWizardStep = 3);
    },
    setPrevStep(state) {
      state.deliveryWizardStep > 1
        ? state.deliveryWizardStep--
        : (state.deliveryWizardStep = 1);
    },
    setEditableVersionIndex(state, action: PayloadAction<number>) {
      state.editableVersionIndex = action.payload;
      if (action.payload !== -1) {
        state.isDirty = true;
      }
    },
    saveVersion(state, action: PayloadAction<any>) {
      state.delivery.Versions[state.editableVersionIndex] = {
        ...state.delivery.Versions[state.editableVersionIndex],
        ...action.payload,
        AdvertisingAgencyAdId: getLoggedUser()?.user?.user?.AdId,
        ExpectedProjectNumber: state.delivery.Number,
        FileType: String(action.payload.FileType),
        FileTypeDescription: Object.entries(FileTypeE).find(
          ([key, value]) => value === action.payload.FileType
        )?.[0],
        LengthDescription: `${convertNumToMinSec(
          action.payload.RequiredLength
        )}/undefined`,
        RequiredLength: String(action.payload.RequiredLength),
      };
      state.editableVersionIndex = -1;
    },
    setHouseNumber(state, action: PayloadAction<string>) {
      state.delivery.Versions[state.editableVersionIndex].HouseNumber =
        action.payload;
    },
    setSponsorshipHouseNumber(state, action: PayloadAction<string>) {
      state.delivery.Versions[
        state.editableVersionIndex
      ].sponsershipHouseNumber = action.payload;
    },
    setCampaignDetails(state, action: PayloadAction<any>) {
      state.campaignDetails = action.payload;
    },
    setIsDirty(state, action: PayloadAction<boolean>) {
      state.isDirty = action.payload;
    },
    setIsShortEdit(state, action: PayloadAction<boolean>) {
      state.isShortEdit = action.payload;
    },
    setSelectedAdvertiser(state, action: PayloadAction<SelectOptionT | null>) {
      state.selectedAdvertiser = action.payload;
    },
    setSelectedBrand(state, action: PayloadAction<SelectOptionT | null>) {
      state.selectedBrand = action.payload;
    },
    setSelectedMediaCompany(state, action: PayloadAction<SelectOptionT | null>) {
      state.selectedMediaCompany = action.payload;
    },
    setSelectedIndustry(state, action: PayloadAction<SelectOptionT | null>) {
      state.selectedIndustry = action.payload;
    },
    setSelectedProductionCompany(
      state,
      action: PayloadAction<SelectOptionT | null>
    ) {
      state.selectedProductionCompany = action.payload;
    },
  },
});

export const {
  setDeliveryWizardStep,
  setDeliveryDescription,
  setVersions,
  setDestinations,
  removerVersionByIndex,
  addVersion,
  addEmptyVersion,
  addDestination,
  removeDestination,
  setDeliveryData,
  setNextStep,
  setPrevStep,
  setEditableVersionIndex,
  saveVersion,
  clearAll,
  setCampaignDetails,
  removeVersionByAdId,
  setIsDirty,
  updateDeliveryData,
  setIsShortEdit,
  setPostProductionCompanyId,
  setProductionCompanyId,
  setProductionLocation,
  setSelectedAdvertiser,
  setSelectedBrand,
  setSelectedMediaCompany,
  setSelectedProductionCompany,
  setSelectedIndustry,
  setHouseNumber,
  setSponsorshipHouseNumber,
} = deliveryWizardSlice.actions;

export default deliveryWizardSlice.reducer;
